const BASE_URL = process.env.REACT_APP_API_URL || 'https://procura-backend-0yj4.onrender.com';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

const request = async (method, path, body = null, isFormData = false) => {
  const options = {
    method,
    headers: isFormData
      ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
      : getHeaders()
  };
  if (body) options.body = isFormData ? body : JSON.stringify(body);
  const res = await fetch(`${BASE_URL}${path}`, options);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const login = (email, password) => request('POST', '/api/auth/login', { email, password });
export const register = (data) => request('POST', '/api/auth/register', data);

// ─── PROJECTS ─────────────────────────────────────────────────────────────────
export const getProjects = () => request('GET', '/api/projects');
export const getProject = (id) => request('GET', `/api/projects/${id}`);
export const getProjectStats = (id) => request('GET', `/api/projects/${id}/stats`);
export const getProjectItems = (id) => request('GET', `/api/projects/${id}/items`);
export const getProjectPhases = (id) => request('GET', `/api/projects/${id}/phases`);
export const getProjectAudit = (id) => request('GET', `/api/projects/${id}/audit`);
export const createProject = (data) => request('POST', '/api/projects', data);
export const updateProject = (id, data) => request('PUT', `/api/projects/${id}`, data);
export const updatePhase = (projectId, phaseId, data) => request('PUT', `/api/projects/${projectId}/phases/${phaseId}`, data);

// ─── PACKAGES ─────────────────────────────────────────────────────────────────
export const getPackages = (projectId) => request('GET', `/api/packages/project/${projectId}`);
export const getPackage = (id) => request('GET', `/api/packages/${id}`);
export const createPackage = (data) => request('POST', '/api/packages', data);
export const updatePackage = (id, data) => request('PUT', `/api/packages/${id}`, data);
export const transitionPackage = (id, state, metadata) => request('POST', `/api/packages/${id}/transition`, { state, metadata });
export const getPackageItems = (id) => request('GET', `/api/packages/${id}/items`);
export const addItemsToPackage = (id, itemIds) => request('POST', `/api/packages/${id}/items`, { item_ids: itemIds });
export const getPackageSupplierMatches = (id) => request('GET', `/api/packages/${id}/supplier-matches`);
export const approveSupplierMatch = (packageId, matchId) => request('POST', `/api/packages/${packageId}/supplier-matches/${matchId}/approve`);
export const getPackageBidLeveling = (id) => request('GET', `/api/packages/${id}/bid-leveling`);
export const getPackageAwardRecommendation = (id) => request('GET', `/api/packages/${id}/award-recommendation`);
export const getPackagePostAward = (id) => request('GET', `/api/packages/${id}/post-award`);
export const updatePostAward = (id, data) => request('PUT', `/api/packages/${id}/post-award`, data);

// ─── DOCUMENTS ────────────────────────────────────────────────────────────────
export const getDocuments = (projectId) => request('GET', `/api/documents/project/${projectId}`);
export const uploadDocument = (projectId, formData) => request('POST', `/api/documents/upload`, formData, true);
export const extractDocument = (documentId) => request('POST', `/api/documents/${documentId}/extract`);
export const getDocumentItems = (documentId) => request('GET', `/api/documents/${documentId}/items`);

// ─── ITEMS ────────────────────────────────────────────────────────────────────
export const getItems = (projectId) => request('GET', `/api/items/project/${projectId}`);
export const updateItem = (id, data) => request('PUT', `/api/items/${id}`, data);
export const reviewItem = (id, status) => request('POST', `/api/items/${id}/review`, { status });
export const bulkAssignItems = (itemIds, packageId) => request('POST', '/api/items/bulk-assign', { item_ids: itemIds, package_id: packageId });
export const exportItemsCSV = (projectId) => `${BASE_URL}/api/items/project/${projectId}/export`;

// ─── SUPPLIERS ────────────────────────────────────────────────────────────────
export const getSuppliers = (params) => request('GET', `/api/suppliers${params ? `?${new URLSearchParams(params)}` : ''}`);
export const getSupplier = (id) => request('GET', `/api/suppliers/${id}`);
export const createSupplier = (data) => request('POST', '/api/suppliers', data);
export const updateSupplier = (id, data) => request('PUT', `/api/suppliers/${id}`, data);
export const getSupplierHistory = (id) => request('GET', `/api/suppliers/${id}/history`);
export const matchSuppliersForPackage = (packageId) => request('POST', `/api/packages/${packageId}/match-suppliers`);

// ─── RFQ ─────────────────────────────────────────────────────────────────────
export const getRFQs = (packageId) => request('GET', `/api/rfq/package/${packageId}`);
export const createRFQ = (data) => request('POST', '/api/rfq', data);
export const approveRFQ = (id, comment) => request('POST', `/api/rfq/${id}/approve`, { comment });
export const sendRFQ = (id) => request('POST', `/api/rfq/${id}/send`);
export const sendRFQReminder = (rfqId, supplierId) => request('POST', `/api/rfq/${rfqId}/remind`, { supplier_id: supplierId });
export const getRFQRecipients = (rfqId) => request('GET', `/api/rfq/${rfqId}/recipients`);

// ─── QUOTES ───────────────────────────────────────────────────────────────────
export const getQuotes = (packageId) => request('GET', `/api/quotes/package/${packageId}`);
export const uploadQuote = (packageId, formData) => request('POST', `/api/quotes/upload`, formData, true);
export const normalizeQuote = (quoteId) => request('POST', `/api/quotes/${quoteId}/normalize`);
export const awardQuote = (quoteId) => request('PUT', `/api/quotes/${quoteId}/award`);
export const runBidLeveling = (packageId) => request('POST', `/api/packages/${packageId}/bid-leveling`);
export const generateAwardRecommendation = (packageId) => request('POST', `/api/packages/${packageId}/award-recommendation`);
export const approveAward = (packageId, recommendationId, comment) => request('POST', `/api/packages/${packageId}/award-recommendation/${recommendationId}/approve`, { comment });

// ─── AUDIT & APPROVALS ────────────────────────────────────────────────────────
export const getAuditLog = (entityType, entityId) => request('GET', `/api/audit/${entityType}/${entityId}`);
export const getPendingApprovals = () => request('GET', '/api/audit/approvals/pending');
export const getEntityApprovals = (entityType, entityId) => request('GET', `/api/audit/approvals/${entityType}/${entityId}`);
export const resolveApproval = (id, status, comment) => request('POST', `/api/audit/approvals/${id}/resolve`, { status, comment });

// ─── AI WORK QUEUE ────────────────────────────────────────────────────────────
export const getWorkQueue = (projectId) => request('GET', `/api/packages/work-queue/${projectId}`);